<script setup>
import { ref } from 'vue';
import { Card } from 'primevue';
import AnButton from '@/components/common/AnButton.vue';
import SIPCalculator from '@/components/calculators/SIPCalculator.vue';
import StepUpSIPCalculator from '@/components/calculators/StepUpSIPCalculator.vue';
import SWPCalculator from '@/components/calculators/SWPCalculator.vue';
import FutureValueCalculator from '@/components/calculators/FutureValueCalculator.vue';
import RentVsBuyCalculator from '@/components/calculators/RentVsBuyCalculator.vue';
import RetirementCorpusCalculator from '@/components/calculators/RetirementCorpusCalculator.vue';

const activeCalculator = ref(null);

// Calculator definitions
const calculators = [
  {
    id: 'sip',
    name: 'SIP Calculator',
    description: 'Calculate future value of your Systematic Investment Plan or required monthly SIP amount.',
    icon: '💰',
    component: 'SIPCalculator'
  },
  {
    id: 'stepup-sip',
    name: 'Step-up SIP Calculator',
    description: 'Calculate future value with annual step-up in SIP amount to beat inflation.',
    icon: '📈',
    component: 'StepUpSIPCalculator'
  },
  {
    id: 'swp',
    name: 'SWP Calculator',
    description: 'Calculate Systematic Withdrawal Plan amount or required corpus for regular income.',
    icon: '💸',
    component: 'SWPCalculator'
  },
  {
    id: 'future-value',
    name: 'Future Value Calculator',
    description: 'Calculate future value of lump sum investment with regular contributions.',
    icon: '🚀',
    component: 'FutureValueCalculator'
  },
  {
    id: 'rent-vs-buy',
    name: 'Rent vs Buy Calculator',
    description: 'Compare the financial impact of renting vs buying a property over time.',
    icon: '🏠',
    component: 'RentVsBuyCalculator'
  },
  {
    id: 'retirement-corpus',
    name: 'Retirement Corpus Calculator',
    description: 'Calculate retirement funding gap and plan your retirement savings strategy.',
    icon: '🏖️',
    component: 'RetirementCorpusCalculator'
  }
];

// Handle calculator selection
const selectCalculator = (calculatorId) => {
  activeCalculator.value = calculatorId;
  // Scroll to calculator section
  setTimeout(() => {
    const calculatorElement = document.getElementById('calculator-section');
    if (calculatorElement) {
      calculatorElement.scrollIntoView({ behavior: 'smooth' });
    }
  }, 100);
};

// Handle back to calculators grid
const backToCalculators = () => {
  activeCalculator.value = null;
  // Scroll to top
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, 100);
};
</script>

<template>
  <div class="planning-tool-container">
    <!-- Header Section -->
    <div class="planning-header-section">
      <p class="main-subtitle">
        Plan your financial future with our comprehensive suite of calculators designed for Indian investors.
      </p>
    </div>

    <!-- Main Content -->
    <div class="planning-content input-shadow br-10 p-20 mt-30">

      <!-- Calculators Grid View -->
      <div v-if="!activeCalculator" class="calculators-grid-section">
        <!-- Calculators Grid -->
        <div class="calculators-grid">
          <div
            v-for="calculator in calculators"
            :key="calculator.id"
            class="calculator-card"
            @click="selectCalculator(calculator.id)"
          >
            <div class="calculator-content">
              <div class="calculator-icon">{{ calculator.icon }}</div>
              <h3 class="calculator-name">{{ calculator.name }}</h3>
              <p class="calculator-description">{{ calculator.description }}</p>
              <div class="calculator-action">
                <AnButton
                  label="Calculate Now"
                  variant="primary"
                  size="medium"
                  class="calculate-button"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Individual Calculator View -->
      <div v-else id="calculator-section" class="calculator-section">
        <div class="calculator-header">
          <button @click="backToCalculators" class="back-calc-button">
            ← Back to Calculators
          </button>
          <h2 class="calculator-title">
            {{ calculators.find(c => c.id === activeCalculator)?.name }}
          </h2>
        </div>

        <!-- Dynamic Calculator Component -->
        <div class="calculator-wrapper">
          <SIPCalculator v-if="activeCalculator === 'sip'" />
          <StepUpSIPCalculator v-else-if="activeCalculator === 'stepup-sip'" />
          <SWPCalculator v-else-if="activeCalculator === 'swp'" />
          <FutureValueCalculator v-else-if="activeCalculator === 'future-value'" />
          <RentVsBuyCalculator v-else-if="activeCalculator === 'rent-vs-buy'" />
          <RetirementCorpusCalculator v-else-if="activeCalculator === 'retirement-corpus'" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Planning Tool Container */
.planning-tool-container {
  max-width: 100%;
}

/* Header Section - matching PortfolioAnalytics style */
.planning-header-section {
  text-align: center;
  margin-bottom: 40px;
  margin-top: 0;
}

.main-headline {
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--an-primary) 0%, #4A90E2 50%, #7B68EE 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 15px;
  margin-top: 0;
  line-height: 1.2;
  letter-spacing: -0.5px;
}

.main-headline::after {
  content: '';
  display: block;
  width: 80px;
  height: 3px;
  background: linear-gradient(135deg, var(--an-primary), #4A90E2);
  margin: 10px auto 0;
  border-radius: 2px;
}

.main-subtitle {
  font-size: 1rem;
  color: var(--an-text-secondary);
  max-width: 600px;
  margin: 4px auto;
  line-height: 1.5;
}

/* Planning Content */
.planning-content {
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Calculators Grid Section */
.calculators-grid-section {
  width: 100%;
}

.calculators-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 0;
}

.calculator-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 100%;
  min-height: 240px;
}

.calculator-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 78, 80, 0.15);
  border-color: var(--an-primary);
  background: white;
}

.calculator-content {
  text-align: center;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.calculator-icon {
  font-size: 2.5rem;
  margin-bottom: 12px;
}

.calculator-name {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin-bottom: 10px;
  line-height: 1.3;
}

.calculator-description {
  color: var(--an-text-secondary);
  line-height: 1.5;
  margin-bottom: 20px;
  flex-grow: 1;
  font-size: 0.9rem;
}

.calculator-action {
  margin-top: auto;
}

.calculate-button {
  width: 100%;
}

/* Individual Calculator Section */
.calculator-section {
  width: 100%;
}

.calculator-header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--an-primary);
}

.back-calc-button {
  padding: 10px 16px;
  background: var(--an-background-light);
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: var(--an-text-primary);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
}

.back-calc-button:hover {
  background: #e2e8f0;
  transform: translateX(-2px);
  border-color: var(--an-primary);
}

.calculator-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin: 0;
}

.calculator-wrapper {
  background: #f8fafc;
  border-radius: 8px;
  padding: 25px;
  border: 1px solid #e2e8f0;
}

/* Color Classes */
.text-success {
  color: var(--an-success);
}

.text-error {
  color: var(--an-error);
}

.text-muted {
  color: var(--an-text-muted);
}

.text-primary {
  color: var(--an-primary);
}

/* Background Colors */
.bg-light {
  background-color: var(--an-background-light);
}

.bg-white {
  background-color: white;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .calculators-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 18px;
  }
}

@media (max-width: 768px) {
  .calculators-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .main-headline {
    font-size: 1.3rem;
  }

  .main-subtitle {
    font-size: 0.95rem;
  }

  .calculator-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .calculator-wrapper {
    padding: 20px;
  }

  .calculator-card {
    padding: 18px;
    min-height: 220px;
  }

  .planning-content {
    padding: 15px;
  }
}

@media (max-width: 480px) {
  .calculator-content {
    padding: 15px;
  }

  .calculator-card {
    min-height: 200px;
    padding: 15px;
  }

  .calculator-name {
    font-size: 1.1rem;
  }

  .calculator-description {
    font-size: 0.85rem;
  }
}
</style>
